import {Injectable} from '@angular/core';
import {MessageBusService} from '../message-bus.service';
import {ShellClientService} from '../shell-communication/shell-client.service';
import {Observable, Subject} from 'rxjs';
import {
    GetOptimalOrderArrangement,
    GetOptimalOrderArrangementProgress,
    GetOptimalOrderArrangementReply,
    OoaPileDto
} from '../shell-communication/ooa-operations';
import {getShortUUID} from '../utils';

// THE OOA TRANSPORT (CR-20260830-002). One service owns the whole async round trip so the three
// consumers (verification dialog, transaction popup, regroup dialog) share ONE in-flight request
// and ONE cached reply: the popup's probe and the dialog's open would otherwise pay two identical
// backend runs seconds apart.
//
// The op is an async command: processCommand returns immediately; the reply arrives over ESS as
// 'GetOptimalOrderArrangementReply', correlated by requestId (the GetAdjustmentSolutions shape -
// see adjustment-pricing-grid.component.ts requestRollAdjustments for the pattern's origin).
//
// ⚠ THE CACHE KEY IS CANONICAL, AND THAT IS LOAD-BEARING. The regroup dialog sorts its source
// groups IN PLACE on open while the popup probes unsorted - byte-comparing raw payloads would
// never match and every open would pay a second round trip. `ooaRequestKey` sorts piles, legs and
// grouping tickets before rendering the key, and the SAME sorted payload is what gets sent, so
// identical screens produce identical wire requests (and identical backend log echoes).

/** Engine worst case (15s full search + 3x45s naked levels; CR-20260901-003 cut the full-closure
 *  budget from 60s) + transport margin. The torn shadow
 *  probe no longer contributes meaningful wall clock: it is skipped when no huge win is reachable
 *  and otherwise stopped at a deterministic 1M-node cap, ~1s worst case (CH-11/CH-12). */
const OOA_GIVE_UP_MS = 210_000;
/** A reply is reusable for the probe->click->open span; quotes move, so keep it short. */
const OOA_SUCCESS_TTL_MS = 60_000;
/** A failure must not be re-requested on every fingerprint judgement - but must retry soon. */
const OOA_REJECTION_TTL_MS = 5_000;

interface PendingRequest {
    resolve: (reply: GetOptimalOrderArrangementReply) => void;
    reject: (error: Error) => void;
    timer: any;
    key: string;
}

interface CacheSlot {
    at: number;
    reply?: GetOptimalOrderArrangementReply;
    error?: Error;
}

@Injectable({providedIn: 'root'})
export class OoaClientService {

    constructor(
        private readonly _shellClient: ShellClientService,
        private readonly _messageBus: MessageBusService
    ) {
        // Root-provided service - lives for the session, no unsubscribe needed.
        this._messageBus.of<GetOptimalOrderArrangementReply>('GetOptimalOrderArrangementReply')
            .subscribe(msg => this.onReply(msg.payload));

        this._messageBus.of<GetOptimalOrderArrangementProgress>('GetOptimalOrderArrangementProgress')
            .subscribe(msg => this.onProgress(msg.payload));
    }

    private readonly _progress = new Subject<GetOptimalOrderArrangementProgress>();

    /**
     * Mid-search milestones (CR-20260901-003) for requests THIS client still has pending -
     * late or foreign progress events are dropped, matching the reply correlation contract.
     * Consumers use this to show truthful spinner text instead of a guessing timer.
     */
    get progress$(): Observable<GetOptimalOrderArrangementProgress> {
        return this._progress.asObservable();
    }

    private onProgress(progress: GetOptimalOrderArrangementProgress): void {

        if (!progress || !this._pending.has(progress.requestId)) {
            return;
        }

        console.log(`[arrange] requestId=${progress.requestId} progress: ` +
            `${progress.expiration}=${progress.milestone}`);

        this._progress.next(progress);
    }

    private readonly _pending = new Map<string, PendingRequest>();
    private readonly _inflight = new Map<string, Promise<GetOptimalOrderArrangementReply>>();
    private readonly _cache = new Map<string, CacheSlot>();

    /**
     * Request the optimal arrangement for the given piles. Coalesces concurrent identical
     * requests and serves a fresh cached reply without a round trip. Rejects on backend error,
     * send failure, or the give-up timeout - the caller treats all three the same way
     * (proceed un-optimized, warn). (The `solutions` count died with revision 2 / CH-9: the
     * two-team contract fixes the result count.)
     */
    request(piles: OoaPileDto[], atm: number | null): Promise<GetOptimalOrderArrangementReply> {

        const canonical = canonicalizePiles(piles);
        const key = ooaRequestKey(canonical, atm);

        const cached = this._cache.get(key);

        if (cached) {
            const ttl = cached.reply ? OOA_SUCCESS_TTL_MS : OOA_REJECTION_TTL_MS;

            if (Date.now() - cached.at <= ttl) {
                return cached.reply
                    ? Promise.resolve(cached.reply)
                    : Promise.reject(cached.error);
            }

            this._cache.delete(key);
        }

        const inflight = this._inflight.get(key);

        if (inflight) {
            return inflight;
        }

        const promise = this.send(canonical, atm, key);
        this._inflight.set(key, promise);

        return promise;
    }

    private async send(piles: OoaPileDto[], atm: number | null,
                       key: string): Promise<GetOptimalOrderArrangementReply> {

        const requestId = 'ooa-' + getShortUUID();

        const resultPromise = new Promise<GetOptimalOrderArrangementReply>((resolve, reject) => {
            const pending: PendingRequest = {
                resolve,
                reject,
                key,
                timer: setTimeout(() => this.giveUp(requestId), OOA_GIVE_UP_MS)
            };
            this._pending.set(requestId, pending);
        });

        console.log(`[arrange] requestId=${requestId} sent: piles=${piles.length} ` +
            `atm=${atm === null || atm === undefined ? 'none' : atm}`);

        try {
            await this._shellClient.processCommand(
                new GetOptimalOrderArrangement(requestId, piles, atm === undefined ? null : atm));
        } catch (e) {
            console.error(`[arrange] requestId=${requestId} send failed`, e);
            this.settle(requestId, undefined, asError(e));
            throw e;
        }

        return resultPromise;
    }

    private onReply(reply: GetOptimalOrderArrangementReply): void {

        if (!reply || !this._pending.has(reply.requestId)) {
            // Not ours or already given up - a late reply is dropped silently, matching the
            // give-up contract: once settled, the answer on record does not change.
            return;
        }

        if (reply.error) {
            console.error(`[arrange] requestId=${reply.requestId} backend reported an error`);
            this.settle(reply.requestId, undefined, new Error('optimal arrangement failed on the backend'));
            return;
        }

        console.log(`[arrange] requestId=${reply.requestId} reply: ` +
            reply.piles.map(p => `${p.expiration}=${p.status}(${p.solutions.length})`).join(', '));

        this.settle(reply.requestId, reply, undefined);
    }

    private giveUp(requestId: string): void {

        if (!this._pending.has(requestId)) {
            return;
        }

        console.error(`[arrange] requestId=${requestId} gave up after ${OOA_GIVE_UP_MS / 1000}s - no reply`);
        this.settle(requestId, undefined, new Error('optimal arrangement did not complete'));
    }

    private settle(requestId: string, reply: GetOptimalOrderArrangementReply | undefined,
                   error: Error | undefined): void {

        const pending = this._pending.get(requestId);

        if (!pending) {
            return;
        }

        this._pending.delete(requestId);
        this._inflight.delete(pending.key);

        if (pending.timer) {
            clearTimeout(pending.timer);
        }

        this._cache.set(pending.key, {at: Date.now(), reply, error});

        if (reply) {
            pending.resolve(reply);
        } else {
            pending.reject(error);
        }
    }
}

function asError(e: any): Error {
    return e instanceof Error ? e : new Error(e + '');
}

/** Sort piles/legs/tickets into the canonical wire order. Pure; inputs are not mutated. */
export function canonicalizePiles(piles: OoaPileDto[]): OoaPileDto[] {
    return (piles || [])
        .map(pile => ({
            expiration: pile.expiration,
            legs: pile.legs.slice().sort(compareLegs),
            currentGrouping: pile.currentGrouping
                .map(t => ({legs: t.legs.slice().sort(compareLegs)}))
                .sort((a, b) => renderLegs(a.legs).localeCompare(renderLegs(b.legs)))
        }))
        .sort((a, b) => a.expiration.localeCompare(b.expiration));
}

/** The cache key: canonical piles + atm. */
export function ooaRequestKey(canonicalPiles: OoaPileDto[], atm: number | null): string {
    return JSON.stringify({piles: canonicalPiles, atm: atm === undefined ? null : atm});
}

function compareLegs(a: {right: string, strike: number, signedQty: number},
                     b: {right: string, strike: number, signedQty: number}): number {
    if (a.right !== b.right) {
        return a.right < b.right ? -1 : 1;
    }
    if (a.strike !== b.strike) {
        return a.strike - b.strike;
    }
    return a.signedQty - b.signedQty;
}

function renderLegs(legs: {right: string, strike: number, signedQty: number}[]): string {
    return legs.map(l => `${l.right}|${l.strike}|${l.signedQty}`).join(',');
}
